import { memoize } from 'underscore';

import {
  ContactStatus,
  SortDirection,
  FlowV2_Statistics,
  GetFlowsV2QueryVariables,
  GetFlowsV2Query,
  FlowData___ConditionRepresentationFragment,
  FlowAction,
} from '~/graphql/types';
import { ascend, descend, isNil, last, path } from 'ramda';
import markedForDeletionContactList from '~/components/page/Contacts/util/markedForDeletionContactList';
import { SortSettings } from '~/hooks/useSortSettings';
import type { IBuilderContext } from '~/components/page/Automation/v2/components/Builder/context';
import getSubject from '~/components/page/Automation/v2/components/UpdateAction/components/Selector/utils/getSubject';
import { getCondition } from '~/components/page/Automation/v2/components/UpdateAction/components/Selector/utils/getConditions';
import getLabelForRep from '~/components/page/Automation/v2/components/UpdateAction/components/ConditionEditorV2/utils/getLabelForRep';

export const isDeleted = (contact: {
  status: ContactStatus | null | undefined;
  id: string;
}): boolean => {
  const markedForDeletionContactIds =
    markedForDeletionContactList.getMarkedForDeletionContacts();

  return (
    (contact.status && contact.status === 'DELETING') ||
    markedForDeletionContactIds.includes(contact.id)
  );
};
export type FlowListData = {
  id: string;
  name: string;
  description?: string | null;
  enabled: boolean;
  createdDate: Date;
  lastModified: Date | null;
  shared: {
    enabled: boolean;
    id: string;
  };
  Statistics: Omit<FlowV2_Statistics, 'leadScoreAverage'> | null;
  hasErrors: boolean;
};

export const composeFlowListData = (
  items: GetFlowsV2Query['getFlowsV2']['items'] | null | undefined,
  variables: GetFlowsV2QueryVariables,
  version: string,
  sortSettings: SortSettings<Array<string>>,
  iBuilderContext: Omit<
    IBuilderContext,
    'initialFlow' | 'offices' | 'users' | 'zapierTriggers'
  >,
): Array<FlowListData> => {
  if (items == null) return [];

  // @ts-ignore I couldn't explain TS what we're doing here, so ignore for now.
  const sorter = (sortSettings.desc ? descend : ascend)(path(sortSettings.id));

  return items
    .map(
      ({
        name,
        description,
        enabled,
        id,
        createdDate,
        lastModified,
        Statistics,
        maximumFlowRun,
        share,
        StartAction,
        ValidationErrors,
      }) => ({
        id,
        name,
        description,
        enabled,
        createdDate: new Date(createdDate),
        lastModified: lastModified?.date ? new Date(lastModified.date) : null,
        Statistics: Statistics || null,
        shared: {
          enabled: !isNil(share),
          id,
        },
        trigger: {
          actionId: StartAction?.id,
          conditionList: StartAction?.conditionList ?? [],
          iBuilderContext: {
            ...iBuilderContext,
            flowBlueprintId: id,
            initialFlow: {
              flowName: name,
              flowDescription: description,
              enabled: enabled,
              maximumFlowRun: maximumFlowRun,
              actions: [],
            },
          },
        },
        hasErrors: ValidationErrors.length > 0,
      }),
    )
    .sort((a, b) => {
      const lastKey = last(sortSettings.id) as string;
      const getFieldValue = value => {
        if (lastKey === 'Statistics') {
          return value.completedFlowInstancesTotal;
        }

        // Sort on Trigger
        if (lastKey === 'trigger') {
          const condition = value.conditionList.conditions[0];
          const representations: Array<FlowData___ConditionRepresentationFragment> | null =
            condition.__typename === 'Flow___SubjectFieldCondition'
              ? getSubject(
                  { type: condition.type, typeId: condition.typeId },
                  iBuilderContext.opts.subjectMap,
                )?.fields.find(({ key }) => condition.field === key)
                  ?.representation ?? null
              : getCondition(condition?.type, iBuilderContext.opts.conditionMap)
                  ?.representation ?? null;

          const asOne = (representations ?? [])
            ?.map(rep =>
              getLabelForRep(
                {
                  representation: rep,
                  condition,
                  ctx: {
                    action: FlowAction.Start,
                    type: 'trigger',
                  },
                },
                {
                  conditionMap: iBuilderContext.opts.conditionMap,
                  directoryMap: iBuilderContext.opts.directoryMap,
                  instanceMap: iBuilderContext.instanceMap,
                  primitiveInputMap: iBuilderContext.primitiveInputMap,
                  subjectMap: iBuilderContext.opts.subjectMap,
                },
              ),
            )
            .map(labelOutput =>
              'result' in labelOutput ? labelOutput.result : labelOutput.error,
            )
            .join(';;');

          return asOne;
        }

        return value;
      };

      const _a = {
        ...a,
        [lastKey]: getFieldValue(path(sortSettings.id, a)),
      };
      const _b = {
        ...b,
        [lastKey]: getFieldValue(path(sortSettings.id, b)),
      };

      return sorter(_a, _b);
    });
};

const memoizedComposeFlowListData = memoize(
  composeFlowListData,
  (
    items: Array<any>,
    variables: GetFlowsV2QueryVariables,
    version: string,
    sortSettings: SortSettings<string>,
  ) => {
    const result = `${version}*@@*${items.length}*@@*${sortSettings.id}*@@*${
      sortSettings.desc ? SortDirection.Desc : SortDirection.Asc
    }*@@*${variables.nextToken || 'null'}`;

    return result;
  },
);

export default memoizedComposeFlowListData;
